import {
  Add,
  CheckCircle,
  KeyboardArrowDown,
  KeyboardArrowUp,
  MoreVert,
  RadioButtonUnchecked,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { useNavigation } from "contexts/notificationsContext";
import {
  obterKanbanAtividades,
  excluirAtividade,
  listarCasosPorAtividade,
  moverAtividade,
  IKanbanAtividades,
  IAtividade,
  ICaso,
} from "services/modules/tarefaDetalhe";
import { ModalAtividade } from "./Modais/ModalAtividade";
import { ModalCaso } from "./Modais/ModalCaso";

// ============================================
// STYLED COMPONENTS
// ============================================

const KanbanContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: theme.spacing(2),
  minHeight: 400,
  maxHeight: 500,
  overflow: "auto",
}));

const ColunaKanban = styled(Box, {
  shouldForwardProp: (prop) => prop !== "statusColor",
})<{ statusColor?: string }>(({ theme, statusColor }) => ({
  backgroundColor: statusColor || "#f5f5f5",
  borderRadius: 8,
  padding: theme.spacing(1),
  display: "flex",
  flexDirection: "column",
  minHeight: 400,
}));

const ColunaHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: theme.spacing(1),
  padding: theme.spacing(0.5, 1),
}));

const ColunaTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.875rem",
  color: "#333",
}));

const CountBadge = styled(Chip)(({ theme }) => ({
  height: 20,
  fontSize: "0.7rem",
  marginLeft: theme.spacing(1),
}));

const CardAtividade = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  cursor: "pointer",
  transition: "box-shadow 0.2s",
  "&:hover": {
    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
  },
}));

const AddButton = styled(Button)(({ theme }) => ({
  borderStyle: "dashed",
  borderColor: "#3C90AD",
  color: "#3C90AD",
  textTransform: "none",
  width: "100%",
  marginTop: theme.spacing(1),
  "&:hover": {
    borderColor: "#2d7a94",
    backgroundColor: "rgba(60, 144, 173, 0.04)",
  },
}));

// ============================================
// INTERFACES
// ============================================

interface TabAtividadesProps {
  idTarefa: number;
  editMode: boolean;
  onAbrirAtividade?: (atividade: IAtividade) => void;
  // Item 7.4: "ADICIONAR ATIVIDADE" só deve aparecer na subtarefa Planificação.
  ehPlanificacao?: boolean;
  // N13.3: referência da tarefa, repassada ao ModalCaso para listar responsáveis cross-empresa.
  idReferencia?: number | null;
}

// ============================================
// CORES POR STATUS
// ============================================

const statusColors: { [key: number]: string } = {
  1: "#e3f2fd", // Novos - Azul claro
  2: "#fff3e0", // Andamento - Laranja claro
  3: "#fce4ec", // Aguardando - Rosa claro
  4: "#e8f5e9", // Concluídos - Verde claro
};

// ============================================
// COMPONENTES DE DRAG & DROP (padrão @dnd-kit)
// ============================================

interface DraggableAtividadeProps {
  idTarefaAtividade: number;
  disabled: boolean;
  children: ReactNode;
}

/** Wrapper que torna o card de atividade arrastável */
const DraggableAtividade = ({
  idTarefaAtividade,
  disabled,
  children,
}: DraggableAtividadeProps) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: `atividade-${idTarefaAtividade}`,
      disabled,
    });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 10 : "auto",
        position: "relative",
      }}
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  );
};

interface DroppableColunaProps {
  idTarefaStatus: number;
  statusColor?: string;
  children: ReactNode;
}

/** Wrapper que torna a coluna do kanban uma área de drop */
const DroppableColuna = ({
  idTarefaStatus,
  statusColor,
  children,
}: DroppableColunaProps) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `coluna-${idTarefaStatus}`,
  });

  return (
    <ColunaKanban
      ref={setNodeRef}
      statusColor={statusColor}
      sx={{
        outline: isOver ? "2px dashed #3C90AD" : "none",
        outlineOffset: -2,
      }}
    >
      {children}
    </ColunaKanban>
  );
};

// ============================================
// COMPONENTE
// ============================================

export const TabAtividades = ({
  idTarefa,
  editMode,
  onAbrirAtividade,
  ehPlanificacao,
  idReferencia = null,
}: TabAtividadesProps) => {
  const { openNotification } = useNavigation();

  const [loading, setLoading] = useState(true);
  const [kanban, setKanban] = useState<IKanbanAtividades | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [atividadeSelecionada, setAtividadeSelecionada] = useState<IAtividade | null>(null);

  // Estados para modal de atividade
  const [modalAtividadeOpen, setModalAtividadeOpen] = useState(false);
  const [atividadeParaEditar, setAtividadeParaEditar] = useState<IAtividade | null>(null);
  const [idStatusInicial, setIdStatusInicial] = useState<number | null>(null);

  // Estados para casos
  const [casosPorAtividade, setCasosPorAtividade] = useState<{ [key: number]: ICaso[] }>({});
  const [loadingCasos, setLoadingCasos] = useState<{ [key: number]: boolean }>({});

  // Estados para modal de caso
  const [modalCasoOpen, setModalCasoOpen] = useState(false);
  const [atividadeParaCaso, setAtividadeParaCaso] = useState<IAtividade | null>(null);
  const [casoParaEditar, setCasoParaEditar] = useState<ICaso | null>(null);

  // Estado para colapsar/expandir colunas (estado local)
  const [colunasColapsadas, setColunasColapsadas] = useState<{ [key: number]: boolean }>({});

  // Sensors para drag and drop (padrão de PainelSubtarefas)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Requer 8px de movimento antes de iniciar drag
      },
    })
  );

  // ============================================
  // CARREGAMENTO
  // ============================================

  const carregarKanban = useCallback(async () => {
    if (!idTarefa) return;

    setLoading(true);
    try {
      const data = await obterKanbanAtividades(idTarefa);
      setKanban(data);

      // Carregar casos para cada atividade
      if (data?.atividades) {
        const casosMap: { [key: number]: ICaso[] } = {};
        for (const atividade of data.atividades) {
          try {
            const casos = await listarCasosPorAtividade(atividade.idTarefaAtividade);
            casosMap[atividade.idTarefaAtividade] = casos;
          } catch (error) {
            console.error(`Erro ao carregar casos da atividade ${atividade.idTarefaAtividade}:`, error);
            casosMap[atividade.idTarefaAtividade] = [];
          }
        }
        setCasosPorAtividade(casosMap);
      }
    } catch (error) {
      console.error("Erro ao carregar kanban de atividades:", error);
    } finally {
      setLoading(false);
    }
  }, [idTarefa]);

  useEffect(() => {
    carregarKanban();
  }, [carregarKanban]);

  // ============================================
  // HANDLERS
  // ============================================

  const handleAdicionarAtividade = (idStatus: number) => {
    if (!editMode) return;

    // Abrir modal de criação de atividade
    setIdStatusInicial(idStatus);
    setAtividadeParaEditar(null);
    setModalAtividadeOpen(true);
  };

  const handleFecharModalAtividade = () => {
    setModalAtividadeOpen(false);
    setAtividadeParaEditar(null);
    setIdStatusInicial(null);
  };

  const handleAtividadeSalva = () => {
    // Item 6.1: ao salvar/concluir a atividade, NÃO fechar o modal — apenas
    // recarregar o kanban. O fechamento ocorre só pelo X/Cancelar
    // (handleFecharModalAtividade, ligado a onClose do ModalAtividade).
    carregarKanban();
  };

  const handleClickAtividade = (atividade: IAtividade) => {
    if (onAbrirAtividade) {
      onAbrirAtividade(atividade);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, atividade: IAtividade) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setAtividadeSelecionada(atividade);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setAtividadeSelecionada(null);
  };

  const handleEditarAtividade = () => {
    if (!atividadeSelecionada) return;

    // Abrir modal de edição com a atividade selecionada
    setAtividadeParaEditar(atividadeSelecionada);
    setIdStatusInicial(null); // Não definir status inicial ao editar
    setModalAtividadeOpen(true);
    handleMenuClose();
  };

  const handleExcluirAtividade = async () => {
    if (!atividadeSelecionada) return;

    if (!window.confirm("Tem certeza que deseja excluir esta atividade?")) {
      handleMenuClose();
      return;
    }

    try {
      await excluirAtividade(atividadeSelecionada.idTarefaAtividade);
      openNotification({
        isOpen: true,
        message: "Atividade excluída com sucesso!",
        type: "success",
      });
      carregarKanban();
    } catch (error: any) {
      openNotification({
        isOpen: true,
        message: error.message || "Erro ao excluir atividade",
        type: "error",
      });
    } finally {
      handleMenuClose();
    }
  };

  // ============================================
  // HANDLERS - MOVER ATIVIDADE (DnD / menu / checkbox)
  // ============================================

  const handleMoverAtividade = async (
    idTarefaAtividade: number,
    idTarefaStatusNovo: number
  ) => {
    try {
      await moverAtividade({ idTarefaAtividade, idTarefaStatusNovo });
      openNotification({
        isOpen: true,
        message: "Atividade movida com sucesso!",
        type: "success",
      });
      await carregarKanban();
    } catch (error: any) {
      openNotification({
        isOpen: true,
        message: error?.message || "Erro ao mover atividade",
        type: "error",
      });
    }
  };

  // Drag & drop entre colunas
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const idAtividade = parseInt(String(active.id).replace("atividade-", ""), 10);
    const idStatusNovo = parseInt(String(over.id).replace("coluna-", ""), 10);
    if (isNaN(idAtividade) || isNaN(idStatusNovo)) return;

    const atividade = kanban?.atividades.find(
      (a) => a.idTarefaAtividade === idAtividade
    );

    // Ignorar drop na coluna de origem ou atividade inexistente
    if (!atividade) return;
    if (atividade.idTarefaStatus === idStatusNovo) return;

    await handleMoverAtividade(idAtividade, idStatusNovo);
  };

  // Checkbox de conclusão: marcar → Concluídos (4); desmarcar → Andamento (2)
  // Item 7.5: só é permitido concluir a atividade se TODOS os casos estiverem
  // concluídos. O backend é a fonte de verdade (retorna HTTP 400 com a mensagem
  // real em `Mensagens`, lida por getErrorsByApi → error.message). Aqui fazemos
  // também uma validação local otimista para feedback imediato.
  const MENSAGEM_CASOS_PENDENTES =
    "Esta atividade ainda possui Casos em aberto. Conclua todos os Casos vinculados a ela antes de marcá-la como Concluída.";

  const handleToggleConclusao = async (atividade: IAtividade) => {
    const concluindo = atividade.idTarefaStatus !== 4;
    const idStatusNovo = concluindo ? 4 : 2;

    // Validação local antes de chamar a API (apenas ao concluir).
    if (concluindo) {
      const casos = casosPorAtividade[atividade.idTarefaAtividade];
      const temCasoPendente = !!casos && casos.some((c) => !c.concluido);
      if (temCasoPendente) {
        openNotification({
          isOpen: true,
          message: MENSAGEM_CASOS_PENDENTES,
          type: "warning",
        });
        // Não chama a API; o checkbox permanece desmarcado pois reflete o
        // estado do kanban (não há toggle otimista local a reverter).
        return;
      }
    }

    // O backend revalida e, se houver caso pendente, devolve 400 com a mensagem
    // real — exibida por handleMoverAtividade (error?.message). Em caso de
    // recusa, o kanban NÃO é recarregado, então o checkbox mantém o estado
    // anterior (não fica "concluído" indevidamente).
    await handleMoverAtividade(atividade.idTarefaAtividade, idStatusNovo);
  };

  // Colapsar/expandir coluna (estado local)
  const handleToggleColuna = (idTarefaStatus: number) => {
    setColunasColapsadas((prev) => ({
      ...prev,
      [idTarefaStatus]: !prev[idTarefaStatus],
    }));
  };

  // ============================================
  // RENDER
  // ============================================

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (!kanban) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <Typography color="textSecondary">
          Não foi possível carregar as atividades
        </Typography>
      </Box>
    );
  }

  // Agrupar atividades por status
  const atividadesPorStatus = kanban.colunas.reduce(
    (acc, coluna) => {
      acc[coluna.idTarefaStatus] = kanban.atividades.filter(
        (a) => a.idTarefaStatus === coluna.idTarefaStatus
      );
      return acc;
    },
    {} as { [key: number]: IAtividade[] }
  );

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" mb={2}>
        Gerencie as atividades relacionadas a esta tarefa. As atividades podem ser organizadas
        em um quadro Kanban com 4 status: Novos, Andamento, Aguardando e Concluídos.
      </Typography>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <KanbanContainer>
          {kanban.colunas.map((coluna) => {
            const colapsada = !!colunasColapsadas[coluna.idTarefaStatus];

            return (
              <DroppableColuna
                key={coluna.idTarefaStatus}
                idTarefaStatus={coluna.idTarefaStatus}
                statusColor={statusColors[coluna.idTarefaStatus] || coluna.cor}
              >
                <ColunaHeader>
                  <Box display="flex" alignItems="center">
                    <ColunaTitle>{coluna.nome}</ColunaTitle>
                    <CountBadge
                      label={atividadesPorStatus[coluna.idTarefaStatus]?.length || 0}
                      size="small"
                      color="primary"
                    />
                  </Box>
                  <Tooltip title={colapsada ? "Expandir coluna" : "Colapsar coluna"}>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleColuna(coluna.idTarefaStatus)}
                    >
                      {colapsada ? (
                        <KeyboardArrowDown fontSize="small" />
                      ) : (
                        <KeyboardArrowUp fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                </ColunaHeader>

                {!colapsada && (
                  <>
                    {/* Botão Adicionar (exceto na coluna Concluídos) */}
                    {/* Item 7.4: só exibir "ADICIONAR ATIVIDADE" na Planificação. */}
                    {editMode && coluna.idTarefaStatus !== 4 && ehPlanificacao && (
                      <AddButton
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => handleAdicionarAtividade(coluna.idTarefaStatus)}
                      >
                        ADICIONAR ATIVIDADE
                      </AddButton>
                    )}

                    {/* Lista de Atividades */}
                    <Box mt={1} flex={1} sx={{ overflowY: "auto" }}>
                      {atividadesPorStatus[coluna.idTarefaStatus]?.map((atividade) => (
                        <DraggableAtividade
                          key={atividade.idTarefaAtividade}
                          idTarefaAtividade={atividade.idTarefaAtividade}
                          disabled={!editMode}
                        >
                          <CardAtividade
                            onClick={() => handleClickAtividade(atividade)}
                          >
                            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  {/* Checkbox de conclusão (marcar → Concluídos; desmarcar → Andamento) */}
                                  {editMode && (
                                    <Tooltip
                                      title={
                                        atividade.idTarefaStatus === 4
                                          ? "Reabrir atividade"
                                          : "Concluir atividade"
                                      }
                                    >
                                      <Checkbox
                                        size="small"
                                        checked={atividade.idTarefaStatus === 4}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={() => handleToggleConclusao(atividade)}
                                        sx={{ p: 0.25 }}
                                      />
                                    </Tooltip>
                                  )}
                                  <Typography variant="body2" fontWeight={500}>
                                    Atividade {atividade.idTarefaAtividade}
                                  </Typography>
                                </Box>
                                {editMode && (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleMenuOpen(e, atividade)}
                                  >
                                    <MoreVert fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>

                              {atividade.descricao && (
                                <Box
                                  sx={{
                                    mt: 1,
                                    "& p": {
                                      margin: 0,
                                      fontSize: "0.75rem",
                                      color: "text.secondary",
                                    },
                                    "& strong, & em": {
                                      fontSize: "0.75rem",
                                    },
                                    "& a": {
                                      color: "primary.main",
                                      textDecoration: "underline",
                                    },
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }}
                                  dangerouslySetInnerHTML={{ __html: atividade.descricao }}
                                />
                              )}

                              {atividade.nomeResponsavel && (
                                <Chip
                                  label={atividade.nomeResponsavel}
                                  size="small"
                                  sx={{ mt: 1, height: 20, fontSize: "0.7rem" }}
                                />
                              )}

                              {/* Lista de Casos */}
                              {casosPorAtividade[atividade.idTarefaAtividade]?.length > 0 && (
                                <Box sx={{ mt: 1.5, pt: 1, borderTop: "1px solid #e0e0e0" }}>
                                  {casosPorAtividade[atividade.idTarefaAtividade].map((caso) => (
                                    <Box
                                      key={caso.idTarefaCaso}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        mb: 0.5,
                                        p: 0.5,
                                        borderRadius: 1,
                                        "&:hover": {
                                          backgroundColor: "rgba(0,0,0,0.02)",
                                        },
                                      }}
                                    >
                                      <Box display="flex" alignItems="center" gap={0.5}>
                                        <Typography variant="caption" color="text.secondary">
                                          Caso {caso.idTarefaCaso}
                                        </Typography>
                                        {caso.descricao && (
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                              maxWidth: 150,
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            - {caso.descricao}
                                          </Typography>
                                        )}
                                      </Box>
                                      <Box display="flex" alignItems="center" gap={0.5}>
                                        <Tooltip title={caso.concluido ? "Concluído" : "Não concluído"} arrow>
                                          {caso.concluido ? (
                                            <CheckCircle
                                              sx={{
                                                fontSize: "1rem",
                                                color: "#4caf50",
                                              }}
                                            />
                                          ) : (
                                            <RadioButtonUnchecked
                                              sx={{
                                                fontSize: "1rem",
                                                color: "#9e9e9e",
                                              }}
                                            />
                                          )}
                                        </Tooltip>
                                        <Button
                                            size="small"
                                            variant="text"
                                            sx={{
                                              minWidth: "auto",
                                              px: 1,
                                              py: 0.25,
                                              fontSize: "0.7rem",
                                              textTransform: "none",
                                            }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setCasoParaEditar(caso);
                                              setAtividadeParaCaso(atividade);
                                              setModalCasoOpen(true);
                                            }}
                                          >
                                            ABRIR
                                          </Button>
                                      </Box>
                                    </Box>
                                  ))}
                                </Box>
                              )}

                              {/* Botão ADICIONAR CASO — N6.1: só na Planificação */}
                              {editMode && ehPlanificacao && (
                                <Box sx={{ mt: 1 }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<Add />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCasoParaEditar(null);
                                      setAtividadeParaCaso(atividade);
                                      setModalCasoOpen(true);
                                    }}
                                    sx={{
                                      width: "100%",
                                      textTransform: "none",
                                      fontSize: "0.75rem",
                                      borderStyle: "dashed",
                                      borderColor: "#3C90AD",
                                      color: "#3C90AD",
                                      "&:hover": {
                                        borderColor: "#2d7a94",
                                        backgroundColor: "rgba(60, 144, 173, 0.04)",
                                      },
                                    }}
                                  >
                                    + ADICIONAR CASO
                                  </Button>
                                </Box>
                              )}
                            </CardContent>
                          </CardAtividade>
                        </DraggableAtividade>
                      ))}
                    </Box>
                  </>
                )}
              </DroppableColuna>
            );
          })}
        </KanbanContainer>
      </DndContext>

      {/* Menu de contexto — reteste 21/08 (item 8): apenas "Abrir" e "Excluir";
          mover entre colunas continua pelo arrastar-e-soltar e pelo checkbox. */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditarAtividade}>
          Abrir
        </MenuItem>
        <MenuItem onClick={handleExcluirAtividade} sx={{ color: "error.main" }}>
          Excluir
        </MenuItem>
      </Menu>

      {/* Modal de Nova/Editar Atividade */}
      <ModalAtividade
        isOpen={modalAtividadeOpen}
        onClose={handleFecharModalAtividade}
        idTarefa={idTarefa}
        atividade={atividadeParaEditar}
        onAtividadeSalva={handleAtividadeSalva}
        idStatusInicial={idStatusInicial || null}
      />

      {/* Modal de Nova/Editar Caso */}
      {atividadeParaCaso && (
        <ModalCaso
          isOpen={modalCasoOpen}
          onClose={() => {
            setModalCasoOpen(false);
            setCasoParaEditar(null);
            setAtividadeParaCaso(null);
          }}
          idTarefaAtividade={atividadeParaCaso.idTarefaAtividade}
          caso={casoParaEditar}
          ehPlanificacao={ehPlanificacao}
          idReferencia={idReferencia}
          // Item 5.C: pré-preenche um novo caso com título/descrição da Atividade.
          atividadeTitulo={atividadeParaCaso.titulo}
          atividadeDescricao={atividadeParaCaso.descricao}
          onCasoSalvo={async () => {
            // Recarregar kanban para atualizar casos
            await carregarKanban();

            // Se foi criação, buscar o caso criado para atualizar o prop
            if (!casoParaEditar && atividadeParaCaso) {
              try {
                // Buscar casos da atividade para obter o mais recente
                const casos = await listarCasosPorAtividade(atividadeParaCaso.idTarefaAtividade);
                if (casos && casos.length > 0) {
                  // Pegar o caso mais recente (último criado)
                  const casoMaisRecente = casos.sort((a, b) =>
                    new Date(b.dataCriacao || 0).getTime() - new Date(a.dataCriacao || 0).getTime()
                  )[0];
                  setCasoParaEditar(casoMaisRecente);
                }
              } catch (error) {
                console.error("Erro ao buscar caso criado:", error);
              }
            }
            // Item 6.1: na edição/conclusão do Caso, NÃO fechar o modal —
            // manter aberto (o kanban já foi recarregado acima). O fechamento
            // ocorre apenas pelo X/Cancelar (onClose do ModalCaso).
          }}
        />
      )}
    </Box>
  );
};
